import { jsx } from 'react/jsx-runtime';
import { AuthenticationProvider } from '@aviv-unified-frontend/runtime/authentication';
import { useBrand, BrandProvider } from '@aviv-unified-frontend/runtime/brand';
import { EnvironmentProvider } from '@aviv-unified-frontend/runtime/environment';
import { FetcherProvider } from '@aviv-unified-frontend/runtime/fetcher';
import { HeadProvider } from '@aviv-unified-frontend/runtime/head';
import { useLocale, I18nProvider } from '@aviv-unified-frontend/runtime/i18n';
import { LifecycleProvider } from '@aviv-unified-frontend/runtime/lifecycle';
import { matchPath, Link, RouterProvider } from '@aviv-unified-frontend/runtime/router';
import { useContext, createContext, forwardRef, useMemo, useCallback } from 'react';
import { MonitoringProvider } from '@aviv-unified-frontend/runtime/monitoring';
import { FeatureFlaggingProvider } from '@aviv-trak/feature-flagging';
import { GeminiProvider } from '@gemini/core';
import { ConsentServiceProvider, useConsentService } from '@aviv-csnt/react';
import { WithThorTestWrapper, WithThor } from '@aviv-dxp/react-thor';

/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ const IS_TEST_ENVIRONMENT = process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB",
    "fr-BE",
    "fr-FR",
    "nl-BE"
];
new Set(LANGUAGE_TAGS_VALUES);
const LANGUAGES = new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>{
    return `❌ An error occurred: ${cause}.\n✅ To solve the issue: ${solution}.`;
};
const IsIntantiatedContext = /*#__PURE__*/ createContext(false);
/**
 * A higher-order-factory-like helper to enforce a single component instance in the tree.
 * @param componentName The component name.
 * @param Component The targetted React component to decorate with the singleton behavior (most of the time, Provider-like component).
 * @returns The decorated component with singleton behavior.
 */ const createSingletonComponent = (componentName, Component)=>{
    return (props)=>{
        const isInstantiated = useContext(IsIntantiatedContext);
        if (isInstantiated) {
            console.error(createErrorMessage({
                cause: `The \`${componentName}\` component cannot be instantiated more than once`,
                solution: `The singleton behavior has been automatically enforced by skipping the nested \`${componentName}\` element creation. To remove the log and prevent any introduced side effect(s), please remove any \`${componentName}\` component instance in your component tree outside the sandbox entry point.\nIf the log still persists, make sure the sandbox entry point is not included in your production bundle`
            }));
            return props.children;
        }
        return /*#__PURE__*/ jsx(IsIntantiatedContext.Provider, {
            value: true,
            children: /*#__PURE__*/ jsx(Component, {
                ...props
            })
        });
    };
};

const isServerSideNavigation = ({ href, isExternal, language, type })=>{
    if (isExternal || type === "server") return true;
    const firstPathSegment = href.split("/").filter(Boolean)[0];
    const urlLocale = firstPathSegment && LANGUAGES.has(firstPathSegment) ? firstPathSegment : false;
    return urlLocale && urlLocale !== language;
};
/**
 * Custom renderer to configure globally all Gemini-like links (including the `Link` component)
 * It's used here to manage different behavior between internal (soft navigation without page refresh also known as client-side navigation)
 * or external links (hard navigation with page refresh also known as server-side navigation).
 *
 * For more details regarding the implementation rationale: see https://avivgroup.atlassian.net/browse/TOS-439
 */ const RenderLink = /*#__PURE__*/ forwardRef(({ isExternal, href, registry, ...restProps }, ref)=>{
    const { language } = useLocale();
    const type = useMemo(()=>{
        const defaultType = "server";
        if (typeof href !== "string") return defaultType;
        const applications = registry?.getApplications() ?? [];
        const matchedApp = applications.find(({ routing })=>routing.paths.some((path)=>matchPath(path, href)));
        if (!matchedApp) return defaultType;
        return matchedApp.routing.type;
    }, [
        registry,
        href
    ]);
    if (isServerSideNavigation({
        href,
        isExternal,
        language,
        type
    })) {
        return(// eslint-disable-next-line no-restricted-syntax
        /*#__PURE__*/ jsx("a", {
            ...restProps,
            target: isExternal ? "_blank" : "_self",
            href: href,
            ref: ref
        }));
    }
    return /*#__PURE__*/ jsx(Link, {
        ...restProps,
        ref: ref,
        to: href
    });
});

const ConfiguredGeminiProvider = ({ routerContext, registry, ...restProps })=>{
    const { languageTag } = useLocale();
    const { createUrl } = useBrand();
    const baseUrl = createUrl("").origin;
    const RenderLinkWithRegistry = useCallback((props)=>/*#__PURE__*/ jsx(RenderLink, {
            ...props,
            registry: registry
        }), [
        registry
    ]);
    return /*#__PURE__*/ jsx(GeminiProvider, {
        ...restProps,
        locale: languageTag,
        renderLink: RenderLinkWithRegistry,
        assetsBaseUrl: baseUrl
    });
};

const ConsentProvider = ({ children, context })=>{
    if (!context) return children;
    return /*#__PURE__*/ jsx(ConsentServiceProvider, {
        id: context.id,
        children: children
    });
};

/**
 * The Thor provider aware of the testing environment
 */ const ThorProvider = ({ children, hasConsentContext })=>{
    // Even if it goes, in theory, against React hook rules, practically conditionally assign the hook is fine since
    // the hook is initialized once at the top React tree level (either the Sandbox or Shell side) and, so, won't change over time.
    const useConsent = hasConsentContext ? useConsentService : ()=>({
            isConsentGranted: false
        });
    /**
   * Consent for Google Analytics 4 is checked at Google Tag Manager level.
   * This variable only tells Thor consent preferences have been saved.
   * When aware of this, Thor starts processing its pending events.
   */ const userConsent = useConsent().isConsentGranted;
    const thorProps = useMemo(()=>({
            userConsent,
            compatibilities: {
                GA3: false,
                GA4: true
            }
        }), [
        userConsent
    ]);
    return /*#__PURE__*/ jsx(WithThorComponent, {
        ...thorProps,
        children: children
    });
};
// TODO: Remove later `IS_TEST_ENVIRONMENT` since it can foster anti pattern (environment divergence)
// This flag is required since, on Thor side, some checks are not implemented properly to handle `parentNode` DOM access in a gracefully way.
const WithThorComponent = IS_TEST_ENVIRONMENT ? WithThorTestWrapper : WithThor;

/**
 * Unified provider to configure a Shell instance (can be either a b2c, b2b or sandbox shell).
 */ const Provider = createSingletonComponent("Provider", ({ children, authenticationContext, brandContext, consentContext, environmentContext, fetcherContext, headContext, i18nContext, lifecycleContext, routerContext, theme, registry, monitoringContext, featureFlaggingContext })=>{
    return /*#__PURE__*/ jsx(BrandProvider, {
        context: brandContext,
        children: /*#__PURE__*/ jsx(EnvironmentProvider, {
            context: environmentContext,
            children: /*#__PURE__*/ jsx(I18nProvider, {
                context: i18nContext,
                children: /*#__PURE__*/ jsx(AuthenticationProvider, {
                    context: authenticationContext,
                    children: /*#__PURE__*/ jsx(HeadProvider, {
                        context: headContext,
                        children: /*#__PURE__*/ jsx(FetcherProvider, {
                            context: fetcherContext,
                            children: /*#__PURE__*/ jsx(RouterProvider, {
                                context: routerContext,
                                children: /*#__PURE__*/ jsx(LifecycleProvider, {
                                    context: lifecycleContext,
                                    children: /*#__PURE__*/ jsx(ConfiguredGeminiProvider, {
                                        theme: theme,
                                        registry: registry,
                                        children: /*#__PURE__*/ jsx(ConsentProvider, {
                                            context: consentContext,
                                            children: /*#__PURE__*/ jsx(FeatureFlaggingProvider, {
                                                context: featureFlaggingContext,
                                                children: /*#__PURE__*/ jsx(MonitoringProvider, {
                                                    context: monitoringContext,
                                                    children: /*#__PURE__*/ jsx(ThorProvider, {
                                                        hasConsentContext: Boolean(consentContext),
                                                        children: children
                                                    })
                                                })
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
    });
});

export { Provider };
